import axios from 'axios';
import { FortuneExchangedItem } from 'src/models/fortune/FortuneExchangedItem';
import { UnlockFlags } from 'src/models/UnlockFlags';
import { Baid } from '../models/Baid';
import { CatalogItemKey } from '../models/catalog/CatalogItemKey';
import { ChatMessageType } from '../models/ChatMessageType';
import { DreamHistory } from '../models/dream/DreamHistory';
import { DreamLand } from '../models/dream/DreamLand';
import { Friend } from '../models/Friend';
import { FriendPresence } from '../models/FriendPresence';
import { LandProfile } from '../models/LandProfile';
import { ReactionItem } from '../models/ReactionItem';
import { ReportCategory } from '../models/ReportCategory';
import { User } from '../models/User';
import { UserProfile } from '../models/UserProfile';
import {
  CatalogFavoriteResponse,
  CatalogItemsResponse,
  CatalogLatestResponse
} from './response/catalog';
import {
  FortuneExchangeableItemsResponse,
  TodaysFortuneResponse,
  TodaysUpdatedFortuneResponse
} from './response/fortune';
import { TipHistoryResponse } from './response/lifeSupports';
import { NewspaperResponse, NewspapersResponse } from './response/newspaper';
import {
  FollowListResponse,
  ResortHistoriesResponse,
  UserResortProfileResponse
} from './response/resort';

// Blanco Server API Documentation is here.
// https://app.dd1.acbaa.mng.nintendo.net/docs/sd

export const baseUrl = process.env.REACT_APP_BLANCO_BASE_URL;
const blancoVersion = process.env.REACT_APP_VERSION;

async function getUserList(): Promise<{ users: User[]; dnt: boolean }> {
  const url = `${baseUrl}${'/api/sd/v1/users'}`;
  const headers: { [key: string]: string } = { 'X-Blanco-Version': `${blancoVersion}` };
  const config = { withCredentials: true, headers };
  try {
    const response = await axios.get(url, config);
    const dnt = response.headers.dnt !== '0';
    const { users } = response.data;
    return { users, dnt };
  } catch (error) {
    throw error;
  }
}

async function postAuthToken(userId: string, gwt?: string): Promise<Baid> {
  const url = `${baseUrl}${'/api/sd/v1/auth_token'}`;
  const data = { userId };
  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'X-Blanco-Version': `${blancoVersion}`
  };
  if (gwt) {
    headers['X-GameWebToken'] = gwt;
  }
  const config = {
    withCredentials: true,
    headers
  };
  try {
    const response = await axios.post(url, data, config);
    const token = response.data.token;
    const expireAt = response.data.expireAt;
    return { token, expireAt };
  } catch (error) {
    throw error;
  }
}

async function getDreamLandHistory(userId: string, baid: string) {
  const path = `/api/sd/v1/users/${userId}/dream_land_history`;
  return requestGet<{ histories: DreamHistory[] }>(path, baid);
}

async function deleteDreamLandHistory(userId: string, dreamAddress: string, baid: string) {
  const path = `/api/sd/v1/users/${userId}/dream_land_history?dream_land_address=${dreamAddress}`;
  return requestDelete(path, baid);
}

async function getDreamLand(userId: string, baid: string) {
  const path = `/api/sd/v1/users/${userId}/dream_land`;
  return requestGet<DreamLand>(path, baid);
}

async function getUserProfile(
  userId: string,
  language: string,
  baid: string
): Promise<UserProfile> {
  const path = `/api/sd/v1/users/${userId}/profile`;
  return requestGet<UserProfile>(path, baid, { language });
}

async function getLandProfile(
  landId: string,
  language: string,
  baid: string
): Promise<LandProfile> {
  const path = `/api/sd/v1/lands/${landId}/profile`;
  return requestGet<LandProfile>(path, baid, { language });
}

async function postMessage(
  message: string,
  type: ChatMessageType,
  destination: string | undefined,
  baid: string
): Promise<{ status?: string }> {
  const path = '/api/sd/v1/messages';
  const data: any = { type, body: message };
  if (destination) {
    data.userId = destination;
  }
  return requestPost(path, data, baid);
}

async function getFriendPresences(baid: string): Promise<{ presences: FriendPresence[] }> {
  const path = '/api/sd/v1/friends/presences';
  return requestGet(path, baid);
}

async function getFriends(baid: string): Promise<{ friends: Friend[] }> {
  const path = '/api/sd/v1/friends';
  return requestGet(path, baid);
}

async function deleteFriend(baid: string, id: string): Promise<void> {
  const path = `/api/sd/v1/friends/${id}`;
  return requestDelete(path, baid);
}

async function getDesigns(
  baid: string
): Promise<{ legacy_designs: Array<{ id: string; data: string }> }> {
  const path = '/api/sd/v1/legacy_designs';
  return requestGet(path, baid);
}

async function uploadDesign(qrTexts: string[], baid: string) {
  const path = '/api/sd/v1/legacy_designs';
  const data = { body: qrTexts };
  return requestPost(path, data, baid);
}

async function deleteDesign(id: string, baid: string) {
  const path = `/api/sd/v1/legacy_designs/${id}`;
  return requestDelete(path, baid);
}

async function getReactions(
  baid: string,
  language: string
): Promise<{ language: string; emoticons: ReactionItem[] }> {
  const path = `/api/sd/v1/emoticons?language=${language}`;
  return requestGet(path, baid);
}

async function postReaction(baid: string, label: string): Promise<{ status?: string }> {
  const path = '/api/sd/v1/messages';
  const data = { type: 'emoticon', body: label };
  return requestPost(path, data, baid);
}

async function getCatalogItems(
  baid: string,
  language: string,
  year: string
): Promise<CatalogItemsResponse> {
  const dataPath = `/api/sd/v1/catalog_items?language=${language}&current_year=${year}`;
  return requestGet(dataPath, baid);
}

async function getCatalogLatest(baid: string): Promise<CatalogLatestResponse> {
  const path = `/api/sd/v1/catalog_items/latest`;
  return requestGet(path, baid);
}

async function getCatalogFavorite(baid: string): Promise<CatalogFavoriteResponse> {
  const path = `/api/sd/v1/catalog_items/favorites`;
  return requestGet(path, baid);
}

async function putCatalogFavorite(itemKey: CatalogItemKey, baid: string): Promise<any> {
  const { unique_id, rebody_index } = itemKey;
  const path = `/api/sd/v1/catalog_items/favorites`;
  const data: any = { unique_id, rebody_index };
  return requestPut(path, data, baid);
}

async function deleteCatalogFavorite(itemKey: CatalogItemKey, baid: string) {
  const { unique_id, rebody_index } = itemKey;
  const query =
    rebody_index !== undefined
      ? `unique_id=${unique_id}&rebody_index=${rebody_index}`
      : `unique_id=${unique_id}`;
  const path = `/api/sd/v1/catalog_items/favorites?${query}`;
  return requestDelete(path, baid);
}

async function getTodaysFortune(date: string, baid: string): Promise<TodaysFortuneResponse> {
  const path = `/api/sd/v1/fortunes?date=${date}`;
  return requestGet(path, baid);
}

async function postTodaysFortune(
  date: string,
  baid: string
): Promise<TodaysUpdatedFortuneResponse> {
  const path = `/api/sd/v1/fortunes`;
  const data = { date };
  return requestPost(path, data, baid);
}

async function getFortunePoint(baid: string): Promise<{ current_point: number }> {
  const path = '/api/sd/v1/fortunes/point';
  return requestGet(path, baid);
}

async function getFortuneExchangeableItems(
  language: string,
  baid: string
): Promise<FortuneExchangeableItemsResponse> {
  const path = `/api/sd/v1/reward_items?language=${language}`;
  return requestGet(path, baid);
}

async function exchangeFortuneItem(
  baid: string,
  date: string,
  unique_id: number,
  remake_id?: number
): Promise<{ current_point: number }> {
  const path = '/api/sd/v1/rewards';
  const data = { date, item_unique_id: unique_id, item_remake_id: remake_id };
  return requestPost(path, data, baid);
}

async function getFortuneExchangeHistory(
  language: string,
  baid: string
): Promise<{ count: number; total_used_point: number; item_histories: FortuneExchangedItem[] }> {
  const path = `/api/sd/v1/rewards/histories?language=${language}`;
  return requestGet(path, baid);
}

async function getMyUserResortProfile(
  baid: string,
  language: string
): Promise<UserResortProfileResponse> {
  const path = `/api/sd/v1/resorts/me?language=${language}`;
  return requestGet(path, baid);
}

async function getOtherUserResortProfile(
  baid: string,
  raid: string,
  language: string
): Promise<UserResortProfileResponse> {
  const path = `/api/sd/v1/resorts?resortPlannerId=${raid}&language=${language}`;
  return requestGet(path, baid);
}

async function getFollowList(baid: string, userId: string): Promise<FollowListResponse> {
  const path = `/api/sd/v1/resort_planner_follows/${userId}`;
  return requestGet(path, baid);
}

async function getResortHistories(baid: string, userId: string): Promise<ResortHistoriesResponse> {
  const path = `/api/sd/v1/resort_planner_histories/${userId}`;
  return requestGet(path, baid);
}

async function deleteResortHistory(baid: string, userId: string, raid: string) {
  const path = `/api/sd/v1/resort_planner_histories/${userId}?id=${raid}`;
  return requestDelete(path, baid);
}

async function followUser(baid: string, userId: string, raid: string) {
  const path = `/api/sd/v1/resort_planner_follows/${userId}`;
  const data = { id: raid };
  return requestPost(path, data, baid);
}

async function unfollowUser(baid: string, userId: string, raid: string) {
  const path = `/api/sd/v1/resort_planner_follows/${userId}?id=${raid}`;
  return requestDelete(path, baid);
}

async function getTipHistories(
  baid: string,
  language: string
): Promise<{ language: string; tips: TipHistoryResponse[] }> {
  const path = `/api/sd/v1/life_supports?language=${language}`;
  return requestGet(path, baid);
}

async function getNewspapers(baid: string): Promise<NewspapersResponse> {
  const path = `/api/sd/v1/newspapers`;
  return requestGet(path, baid);
}

async function getNewspaper(
  findKey: string,
  requestedAt: string,
  language: string,
  baid: string
): Promise<NewspaperResponse> {
  const path = `/api/sd/v1/newspapers/${findKey}?requestedAt=${requestedAt}&language=${language}`;
  return requestGet(path, baid);
}

async function getNewspaperLatest(
  requestedAt: string,
  language: string,
  baid: string
): Promise<NewspaperResponse> {
  const path = `/api/sd/v1/newspapers/latest?requestedAt=${requestedAt}&language=${language}`;
  return requestGet(path, baid);
}

async function patchNewspaper(findKey: string, mainArticleIndexes: number[], baid: string) {
  const path = `/api/sd/v1/newspapers/${findKey}`;
  const body = { mainArticleIndexes };
  return requestPatch(path, body, baid);
}

async function deleteNewspaperContent(findKey: string, contentId: string, baid: string) {
  const path = `/api/sd/v1/newspapers/${findKey}?contentId=${contentId}`;
  return requestDelete(path, baid);
}

async function getUnlockFlags(baid: string): Promise<UnlockFlags> {
  const path = '/api/sd/v1/flags';
  return requestGet(path, baid);
}

async function reportSpam(
  type: 'user_profile' | 'resort',
  category: ReportCategory,
  reason: string | undefined,
  language: string,
  contentId: string,
  digest: string,
  createdAt: number,
  baid: string
) {
  const path = `/api/sd/v1/reports`;
  const data = {
    type,
    category,
    reason,
    language,
    ugc_data: {
      id: contentId,
      digest,
      created_at: createdAt
    }
  };
  return requestPost(path, data, baid);
}

export default {
  getUserList,
  postAuthToken,
  getDreamLandHistory,
  deleteDreamLandHistory,
  getDreamLand,
  getUserProfile,
  getLandProfile,
  postMessage,
  getFriends,
  getFriendPresences,
  deleteFriend,
  getDesigns,
  uploadDesign,
  deleteDesign,
  getReactions,
  postReaction,
  getCatalogItems,
  getCatalogLatest,
  getCatalogFavorite,
  putCatalogFavorite,
  deleteCatalogFavorite,
  getTodaysFortune,
  postTodaysFortune,
  getFortunePoint,
  getFortuneExchangeableItems,
  exchangeFortuneItem,
  getFortuneExchangeHistory,
  getUnlockFlags,
  getMyUserResortProfile,
  getFollowList,
  getOtherUserResortProfile,
  getResortHistories,
  deleteResortHistory,
  followUser,
  unfollowUser,
  getTipHistories,
  getNewspapers,
  getNewspaper,
  getNewspaperLatest,
  patchNewspaper,
  deleteNewspaperContent,
  reportSpam
};

function authHeader(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Blanco-Version': `${blancoVersion}`
  };
}

async function requestGet<T>(path: string, token: string, params?: any): Promise<T> {
  const config = {
    headers: authHeader(token),
    params
  };
  try {
    const response = await axios.get(`${baseUrl}${path}`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function requestPost(path: string, body: any, token: string): Promise<any> {
  const config = {
    headers: authHeader(token)
  };
  try {
    const response = await axios.post(`${baseUrl}${path}`, body, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function requestPut(path: string, body: any, token: string): Promise<any> {
  const config = {
    headers: authHeader(token)
  };
  try {
    const response = await axios.put(`${baseUrl}${path}`, body, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function requestDelete(path: string, token: string): Promise<any> {
  const config = {
    headers: authHeader(token)
  };
  try {
    const response = await axios.delete(`${baseUrl}${path}`, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async function requestPatch(path: string, body: any, token: string): Promise<any> {
  const config = {
    headers: authHeader(token)
  };
  try {
    const response = await axios.patch(`${baseUrl}${path}`, body, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}
